import React from 'react';

interface WifiIconProps {
  rssi: number;
  color: string;
}

// RSSI (Received Signal Strength Indicator) thresholds for signal bars
// Reference: -30 dBm (excellent) to -90 dBm (weak)
const RSSI_EXCELLENT = -50;
const RSSI_GOOD = -60;
const RSSI_FAIR = -70;
const RSSI_WEAK = -85;

/**
 * Calculates the number of active WiFi signal bars based on RSSI value
 * @param rssi - Signal strength in dBm (typically -30 to -90)
 * @returns Number of active bars (0-4)
 */
function calculateActiveBars(rssi: number): number {
  if (rssi >= RSSI_EXCELLENT) return 4;
  if (rssi >= RSSI_GOOD) return 3;
  if (rssi >= RSSI_FAIR) return 2;
  if (rssi >= RSSI_WEAK) return 1;
  return 0;
}

/**
 * Dynamic SVG WiFi signal icon that displays signal strength based on RSSI
 * Shows 4 arcs with opacity based on signal strength
 */
function WifiIcon({ color, rssi }: WifiIconProps): React.ReactElement {
  const activeBars = calculateActiveBars(rssi);

  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ transition: 'stroke 0.3s ease' }}
    >
      {/* Central point */}
      <path d="M12 20h.01" />

      {/* Signal bar 1: innermost arc */}
      <path
        d="M9 17a3.5 3.5 0 0 1 6 0"
        opacity={activeBars >= 1 ? 1 : 0.2}
        style={{ transition: 'opacity 0.3s' }}
      />

      {/* Signal bar 2: middle arc */}
      <path
        d="M6 14a7 7 0 0 1 12 0"
        opacity={activeBars >= 2 ? 1 : 0.2}
        style={{ transition: 'opacity 0.3s' }}
      />

      {/* Signal bar 3: outermost arc */}
      <path
        d="M3 11a11 11 0 0 1 18 0"
        opacity={activeBars >= 3 ? 1 : 0.2}
        style={{ transition: 'opacity 0.3s' }}
      />
    </svg>
  );
}

export default WifiIcon;
